import type { ElementType } from 'react';
import React, { useLayoutEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { StyledReactQuill, RTEWrapper } from './styled-component';
import RichTextEditorMeta from './rte-meta';
import { convertMarkdownToHTML, convertHtmlToMarkdown, detectLanguageDirection } from '../utils/utils';
import { useLoadStyles } from '../hook/useLoadStyles';
export const TEXT_FORMAT = ({
  MARKDOWN: 'markdown',
  HTML: 'html'
} as const);
type RTEFormatT = {
  markdown: string;
  text: string;
  html: string;
};
type TextEventHandlerT = ({
  markdown,
  text,
  html
}: RTEFormatT) => void;
export type RTEProps = {
  text: string;
  onChange: TextEventHandlerT;
  onBlur?: TextEventHandlerT;
  styles?: {
    maxHeight?: string;
    minHeight?: string;
  };
  placeholder?: string;
  readOnly?: boolean;
  enableAutoFocusForRTE?: boolean;
  config?: {
    disablePaste?: boolean;
  };
  showMeta?: boolean;
  textFormat?: (typeof TEXT_FORMAT)[keyof typeof TEXT_FORMAT];
};
export const RichTextEditor = ({
  text,
  onChange,
  onBlur,
  styles,
  placeholder = 'Enter text',
  readOnly = false,
  enableAutoFocusForRTE = true,
  config = {
    disablePaste: false
  },
  showMeta = true,
  textFormat = TEXT_FORMAT.MARKDOWN
}: RTEProps) => {
  const transformedText = textFormat === TEXT_FORMAT.HTML ? DOMPurify.sanitize(text) : convertMarkdownToHTML(text);
  const [ReactQuill, setReactQuill] = React.useState<ElementType | null>(null);
  const [updatedText, setUpdatedText] = React.useState<string>(transformedText);
  useLoadStyles();
  const quillParentRef = useRef<HTMLElement | null>(null);

  // this is done to solve the import of quill on SSR application using rte
  const loadReactQuillComponent = async () => {
    const loadResult = await import('./react-quill');
    setReactQuill(() => loadResult.default);
  };
  useLayoutEffect(() => {
    loadReactQuillComponent();
  }, []);
  const sendInputEvent = ({
    html,
    text
  }: Omit<RTEFormatT, 'markdown'>, eventFn: TextEventHandlerT) => {
    html = html.replaceAll('<p></p>', '<br>');
    const sanitizedHTML = DOMPurify.sanitize(html);
    const markdown = convertHtmlToMarkdown(sanitizedHTML);
    eventFn({
      markdown,
      text: text,
      html: sanitizedHTML
    });
    setUpdatedText(text);
  };
  return <RTEWrapper>
      <StyledReactQuill $styles={styles} className="quill" ref={quillParentRef}>
        {ReactQuill && <ReactQuill placeholder={placeholder} defaultValue={transformedText} onChange={(formats: RTEFormatT) => sendInputEvent(formats, onChange)} onBlur={(formats: RTEFormatT) => sendInputEvent(formats, onBlur || (() => {}))} readOnly={readOnly} enableAutoFocusForRTE={enableAutoFocusForRTE} direction={detectLanguageDirection(text)} bounds={quillParentRef.current} config={config} />}
      </StyledReactQuill>
      {showMeta && <RichTextEditorMeta text={updatedText} />}
    </RTEWrapper>;
};